export interface WeatherData {
    temp: number;
    condition: string;
    conditionText: string;
    icon: string;
    cityName: string;
    isRaining: boolean;
    alertLevel: 'normal' | 'warning' | 'critical';
    humidity?: number;
    windSpeed?: number;
}

type HgWeatherResponse = {
    by?: string;
    valid_key?: boolean;
    results?: {
        temp?: number;
        city_name?: string;
        description?: string;
        condition_slug?: string;
        currently?: string;
        humidity?: number;
        wind_speedy?: string;
    };
};

type HgWeatherOnlyResults = {
    temp?: number;
    city_name?: string;
    description?: string;
    condition_slug?: string;
    currently?: string;
    humidity?: number;
    wind_speedy?: string;
};

const extractWindSpeed = (value?: string): number | undefined => {
    if (!value) return undefined;
    const matched = value.match(/[\d.,]+/);
    if (!matched) return undefined;
    const parsed = Number(matched[0].replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : undefined;
};

const resolveAlertLevel = (conditionText: string, isRaining: boolean, temp: number): 'normal' | 'warning' | 'critical' => {
    const text = conditionText.toLowerCase();
    const isStorm = /tempest|trovoada|storm|granizo/.test(text);
    if (isStorm) return 'critical';
    if (isRaining || temp >= 35 || temp <= 10) return 'warning';
    return 'normal';
};

export const getCurrentWeather = async (lat?: number, lng?: number, apiKey?: string): Promise<WeatherData | null> => {
    // Validate required parameters
    if (!apiKey || apiKey.trim() === '') {
        console.warn('[WeatherService] API key not configured or empty');
        return null;
    }
    if (!lat || !lng) {
        console.warn('[WeatherService] Missing coordinates:', { lat, lng });
        return null;
    }

    const endpoint = new URL('https://api.hgbrasil.com/weather');
    endpoint.searchParams.set('key', apiKey.trim());
    endpoint.searchParams.set('lat', String(lat));
    endpoint.searchParams.set('lon', String(lng));
    endpoint.searchParams.set('user_ip', 'remote');
    endpoint.searchParams.set('fields', 'only_results,temp,city_name,description,condition_slug,currently,humidity,wind_speedy');

    try {
        const response = await fetch(endpoint.toString(), {
            method: 'GET',
            headers: { Accept: 'application/json' }
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error('[WeatherService] API error:', { status: response.status, body: errorText });
            return null;
        }

        const payload = (await response.json()) as HgWeatherResponse | HgWeatherOnlyResults;
        const wrapped = payload as HgWeatherResponse;
        const data = wrapped.results ?? (payload as HgWeatherOnlyResults);

        if (!data || typeof data.temp !== 'number') {
            console.warn('[WeatherService] Invalid data format or missing temp:', data);
            return null;
        }

        const conditionText = data.description || 'Sem dados';
        const isRaining = /rain|chuva|garoa|storm|tempest/.test(conditionText.toLowerCase());

        return {
            temp: data.temp,
            condition: data.condition_slug || conditionText,
            conditionText,
            icon: data.condition_slug || 'unknown',
            cityName: data.city_name || 'Local',
            isRaining,
            alertLevel: resolveAlertLevel(conditionText, isRaining, data.temp),
            humidity: data.humidity,
            windSpeed: extractWindSpeed(data.wind_speedy)
        };
    } catch (error) {
        console.error('[WeatherService] Fetch error:', error instanceof Error ? error.message : error);
        return null;
    }
};
